import React, { useState } from "react";

import { IconCloud3D } from "../../../components/animations/IconCloud3D.jsx";
import { ArrowUpRight } from "lucide-react";
import WavyText from "../../../components/animations/WavyText.jsx";
import ContactModal from "../../../Assets/Common/ContactModal";

/* -------------------------------------------------------------------------- */
/* TESTIMONIAL DATA                                                           */
/* -------------------------------------------------------------------------- */

const testimonials = [
  {
    name: "Devon Lane",
    role: "Co. Founder",
    text: "Nexor truly exceeded our expectations. From initial consultation to final implementation, their team provided excellent service.",
    img: "https://i.pravatar.cc/150?u=devon",
  },
  {
    name: "Caeron Williason",
    role: "Co. Founder",
    text: "Their ability to integrate exceeded expectations. The support was unwavering throughout the journey.",
    img: "https://i.pravatar.cc/150?u=caeron",
  },
  {
    name: "Eleanor Pena",
    role: "Co. Founder",
    text: "A transformation journey that was seamless and professional. Highly recommended for future migrations.",
    img: "https://i.pravatar.cc/150?u=eleanor",
  },
  {
    name: "Marlan Horn",
    role: "Co. Founder",
    text: "Excellent service and unwavering support. Nexor truly knows how to transform businesses through innovation.",
    img: "https://i.pravatar.cc/150?u=marlan",
  },
];

/* -------------------------------------------------------------------------- */
/* TECHNOLOGY ICONS                                                           */
/* -------------------------------------------------------------------------- */

const slugs = [
  "typescript",
  "javascript",
  "react",
  "nodedotjs",
  "nextdotjs",
  "amazonaws",
  "postgresql",
  "firebase",
  "vercel",
  "docker",
  "git",
  "github",
  "figma",
  "python",
  "go",
  "rust",
];

const brandColors = {
  typescript: "3178C6",
  javascript: "F7DF1E",
  react: "61DAFB",
  nodedotjs: "5FA04E",
  nextdotjs: "FFFFFF",
  amazonaws: "FF9900",
  postgresql: "4169E1",
  firebase: "FFCA28",
  vercel: "FFFFFF",
  docker: "2496ED",
  git: "F05032",
  github: "FFFFFF",
  figma: "F24E1E",
  python: "3776AB",
  go: "00ADD8",
  rust: "000000",
};

const images = slugs.map(
  (slug) =>
    `https://cdn.simpleicons.org/${slug}/${brandColors[slug] || "FFFFFF"}`
);

/* -------------------------------------------------------------------------- */
/* TESTIMONIAL CARD                                                           */
/* -------------------------------------------------------------------------- */

const TestimonialCard = ({
  name,
  role,
  text,
  img,
  mobile = false,
}) => {
  return (
    <article
      className={`
        group
        shrink-0
        rounded-3xl
        border
        theme-border
        theme-card
        shadow-xl
        transition-all
        duration-500
        hover:-translate-y-1
        hover:border-[var(--brand-primary)]
        hover:shadow-[0_15px_45px_var(--shadow-color)]

        ${
          mobile
            ? `
              w-[78vw]
              max-w-[310px]
              min-h-[165px]
              rounded-2xl
              p-4
              min-[360px]:w-[76vw]
              min-[400px]:w-[72vw]
              min-[480px]:w-[65vw]
            `
            : `
              w-full
              p-5
              xl:p-6
            `
        }
      `}
    >
      {/* ------------------------------------------------------------------ */}
      {/* RATING                                                             */}
      {/* ------------------------------------------------------------------ */}

      <div
        className={`
          flex
          items-center
          font-bold
          tracking-tighter
          brand-color
          ${mobile ? "mb-2 text-[10px]" : "mb-2 text-xs"}
        `}
      >
        <span>★★★★★</span>

        <span
          className={`
            ml-2
            theme-text-muted
            ${mobile ? "text-[10px]" : ""}
          `}
        >
          5.0/5
        </span>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* TESTIMONIAL                                                         */}
      {/* ------------------------------------------------------------------ */}

      <p
        className={`
          theme-text-secondary
          leading-relaxed

          ${
            mobile
              ? `
                mb-4
                line-clamp-3
                text-[11px]
                leading-[1.5]
              `
              : `
                mb-6
                text-sm
                leading-6
              `
          }
        `}
      >
        "{text}"
      </p>

      {/* ------------------------------------------------------------------ */}
      {/* USER                                                                */}
      {/* ------------------------------------------------------------------ */}

      <div className="flex items-center gap-3">
        <img
          src={img}
          alt={name}
          loading="lazy"
          draggable={false}
          className={`
            rounded-full
            border
            theme-border
            grayscale
            object-cover
            transition
            duration-300
            group-hover:grayscale-0
            ${mobile ? "h-9 w-9" : "h-11 w-11"}
          `}
        />

        <div className="min-w-0">
          <h4
            className={`
              truncate
              font-bold
              tracking-wide
              theme-text-primary
              ${mobile ? "text-[11px]" : "text-sm"}
            `}
          >
            {name}
          </h4>

          <p
            className={`
              font-medium
              theme-text-muted
              ${mobile ? "text-[9px]" : "text-xs"}
            `}
          >
            {role}
          </p>
        </div>
      </div>
    </article>
  );
};

/* -------------------------------------------------------------------------- */
/* CLIENT SECTION                                                             */
/* -------------------------------------------------------------------------- */

const ClientSection = () => {
  /* ------------------------------------------------------------------------ */
  /* CONTACT MODAL STATE                                                      */
  /* ------------------------------------------------------------------------ */

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const duplicatedTestimonials = [
    ...testimonials,
    ...testimonials,
    ...testimonials,
  ];

  return (
    <>
      <section
        className="
          relative
          w-full
          overflow-hidden
          theme-bg-primary
          font-saira
          theme-text-primary
          px-4
          py-4

          min-[380px]:px-5
          min-[380px]:py-5

          sm:px-6
          sm:py-6

          md:px-8
          md:py-7

          lg:px-10
          lg:py-5

          xl:px-12
          xl:py-6
        "
      >
        {/* ================================================================== */}
        {/* MAIN CONTENT                                                       */}
        {/* ================================================================== */}

        <div
          className="
            relative
            z-10
            mx-auto
            flex
            w-full
            max-w-[1500px]
            flex-col
            items-center
            gap-5

            sm:gap-6

            md:gap-6

            lg:flex-row
            lg:items-center
            lg:gap-7

            xl:gap-9
          "
        >
          {/* ================================================================== */}
          {/* TESTIMONIAL AREA                                                   */}
          {/* ================================================================== */}

          <div
            className="
              relative
              w-full
              min-w-0

              lg:w-[58%]

              xl:w-[60%]
            "
          >
            {/* ================================================================ */}
            {/* DESKTOP - TWO VERTICAL COLUMNS                                   */}
            {/* ================================================================ */}

            <div
              className="
                relative
                hidden
                h-[500px]
                w-full
                gap-5
                overflow-hidden
                mask-fade-vertical

                lg:flex

                xl:h-[500px]
              "
            >
              {/* Column 1 */}

              <div
                className="
                  flex
                  w-1/2
                  shrink-0
                  flex-col
                  space-y-4
                  animate-marquee-v
                "
              >
                {duplicatedTestimonials.map((testimonial, index) => (
                  <TestimonialCard
                    key={`desktop-col1-${index}`}
                    {...testimonial}
                  />
                ))}
              </div>

              {/* Column 2 */}

              <div
                className="
                  flex
                  w-1/2
                  shrink-0
                  flex-col
                  space-y-4
                  pt-14
                  animate-marquee-v-reverse
                "
              >
                {duplicatedTestimonials.map((testimonial, index) => (
                  <TestimonialCard
                    key={`desktop-col2-${index}`}
                    {...testimonial}
                  />
                ))}
              </div>
            </div>

            {/* ================================================================ */}
            {/* TABLET - TWO VERTICAL COLUMNS                                   */}
            {/* ================================================================ */}

            <div
              className="
                relative
                hidden
                h-[420px]
                w-full
                gap-4
                overflow-hidden
                mask-fade-vertical

                md:flex

                lg:hidden
              "
            >
              {/* Tablet Column 1 */}

              <div
                className="
                  flex
                  w-1/2
                  shrink-0
                  flex-col
                  space-y-4
                  animate-marquee-v
                "
              >
                {duplicatedTestimonials.map((testimonial, index) => (
                  <TestimonialCard
                    key={`tablet-col1-${index}`}
                    {...testimonial}
                  />
                ))}
              </div>

              {/* Tablet Column 2 */}

              <div
                className="
                  flex
                  w-1/2
                  shrink-0
                  flex-col
                  space-y-4
                  pt-12
                  animate-marquee-v-reverse
                "
              >
                {duplicatedTestimonials.map((testimonial, index) => (
                  <TestimonialCard
                    key={`tablet-col2-${index}`}
                    {...testimonial}
                  />
                ))}
              </div>
            </div>

            {/* ================================================================ */}
            {/* MOBILE - TWO HORIZONTAL ROWS                                     */}
            {/* ================================================================ */}

            <div
              className="
                relative
                block
                w-[calc(100%+32px)]
                -ml-4
                overflow-hidden

                min-[380px]:w-[calc(100%+40px)]
                min-[380px]:-ml-5

                sm:w-[calc(100%+48px)]
                sm:-ml-6

                md:hidden
              "
            >
              {/* Left Fade */}

              <div
                className="
                  pointer-events-none
                  absolute
                  inset-y-0
                  left-0
                  z-20
                  w-8
                  bg-gradient-to-r
                  from-[var(--bg-primary)]
                  via-[color-mix(in_srgb,var(--bg-primary)_70%,transparent)]
                  to-transparent
                "
              />

              {/* Right Fade */}

              <div
                className="
                  pointer-events-none
                  absolute
                  inset-y-0
                  right-0
                  z-20
                  w-8
                  bg-gradient-to-l
                  from-[var(--bg-primary)]
                  via-[color-mix(in_srgb,var(--bg-primary)_70%,transparent)]
                  to-transparent
                "
              />

              {/* -------------------------------------------------------------- */}
              {/* MOBILE ROW 1                                                   */}
              {/* -------------------------------------------------------------- */}

              <div className="relative mb-2.5 overflow-hidden">
                <div className="flex w-max gap-3 px-4 animate-marquee-horizontal-left">
                  {duplicatedTestimonials.map((testimonial, index) => (
                    <TestimonialCard
                      key={`mobile-row1-${index}`}
                      {...testimonial}
                      mobile
                    />
                  ))}
                </div>
              </div>

              {/* -------------------------------------------------------------- */}
              {/* MOBILE ROW 2                                                   */}
              {/* -------------------------------------------------------------- */}

              <div className="relative overflow-hidden">
                <div className="flex w-max gap-3 px-4 animate-marquee-horizontal-right">
                  {duplicatedTestimonials
                    .slice()
                    .reverse()
                    .map((testimonial, index) => (
                      <TestimonialCard
                        key={`mobile-row2-${index}`}
                        {...testimonial}
                        mobile
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* ================================================================== */}
          {/* RIGHT CONTENT                                                       */}
          {/* ================================================================== */}

          <div
            className="
              z-20
              flex
              w-full
              min-w-0
              flex-col
              items-center
              text-center
              gap-4

              sm:gap-5

              md:gap-6

              lg:w-[42%]
              lg:items-start
              lg:text-left
              lg:gap-5

              xl:w-[40%]
              xl:gap-4
            "
          >
            {/* ================================================================ */}
            {/* QUOTE ICON                                                        */}
            {/* ================================================================ */}

            <div
              className="
                inline-flex
                h-11
                w-11
                shrink-0
                items-center
                justify-center
                rounded-full
                brand-bg
                text-[var(--bg-primary)]
                ring-1
                ring-[var(--brand-primary)]/50
                shadow-[0_0_30px_var(--shadow-color)]
                transition-all
                duration-500

                sm:h-12
                sm:w-12

                md:h-13
                md:w-13
              "
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="
                  h-5
                  w-5

                  sm:h-6
                  sm:w-6
                "
              >
                <path
                  d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C20.1216 16 21.017 16.8954 21.017 18V21C21.017 22.1046 20.1216 23 19.017 23H16.017C14.9124 23 14.017 22.1046 14.017 21ZM3 21L3 18C3 16.89543 3.89543 16 5 16H8C9.10457 16 10 16.89543 10 18V21C10 22.1046 9.10457 23 8 23H5C3.89543 23 3 22.1046 3 21Z"
                  opacity="0.3"
                />

                <path d="M11.1 11.1C11.1 8.2 12.8 5.7 15.4 4.3L16.1 5.7C14.4 6.7 13.3 8.3 13.1 10.1H15.8V15.8H10.1V10.1L11.1 11.1ZM3.1 11.1C3.1 8.2 4.8 5.7 7.4 4.3L8.1 5.7C6.4 6.7 5.3 8.3 5.1 10.1H7.8V15.8H2.1V10.1L3.1 11.1Z" />
              </svg>
            </div>

            {/* ================================================================ */}
            {/* HEADING                                                           */}
            {/* ================================================================ */}

            <h2
              className="
                w-full
                text-3xl
                font-bold
                leading-[1.05]
                tracking-tight
                theme-text-primary

                min-[380px]:text-[32px]

                sm:text-4xl

                md:text-4xl

                lg:text-[42px]

                xl:text-5xl
              "
            >
              <WavyText text="Hear From" />
              <br />
              <WavyText text="Clients" />
            </h2>

            {/* ================================================================ */}
            {/* DESCRIPTION                                                       */}
            {/* ================================================================ */}

            <p
              className="
                w-full
                max-w-[500px]
                text-sm
                leading-[1.65]
                theme-text-muted

                sm:text-base
                sm:leading-[1.7]

                md:text-lg

                lg:max-w-md
                lg:text-base
                lg:leading-7

                xl:text-lg
              "
            >
              We are dedicated to transforming businesses through innovative
              and future-ready migration and storage solutions.
            </p>

            {/* ================================================================ */}
            {/* CONTACT BUTTON                                                    */}
            {/* ================================================================ */}

            <button
              type="button"
              onClick={() => setIsContactModalOpen(true)}
              className="
                group
                relative
                inline-flex
                w-fit
                overflow-hidden
                rounded-full
                border
                border-transparent
                brand-bg
                px-4
                py-2
                font-semibold
                text-[var(--bg-primary)]
                shadow-md
                transition-all
                duration-300

                hover:scale-105
                hover:border-[var(--brand-primary)]
                hover:text-[var(--text-primary)]
                hover:shadow-xl

                active:scale-95

                sm:px-5
                sm:py-2.5
              "
            >
              {/* Hover Background */}

              <span
                aria-hidden="true"
                className="
                  absolute
                  inset-0
                  w-0
                  theme-bg-primary
                  transition-all
                  duration-500
                  ease-out
                  group-hover:w-full
                "
              />

              {/* Button Content */}

              <span
                className="
                  relative
                  z-10
                  flex
                  items-center
                  gap-2
                  text-sm

                  sm:gap-3
                  sm:text-base

                  md:text-lg
                "
              >
                Contact Us

                <span
                  className="
                    inline-flex
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    theme-bg-primary
                    p-1.5
                    theme-text-primary
                    transition-all
                    duration-500

                    group-hover:translate-x-1
                    group-hover:rotate-45
                    group-hover:brand-bg
                    group-hover:text-[var(--bg-primary)]

                    sm:p-2
                  "
                >
                  <ArrowUpRight
                    className="
                      h-4
                      w-4

                      sm:h-5
                      sm:w-5
                    "
                    strokeWidth={2.3}
                  />
                </span>
              </span>
            </button>

            {/* ================================================================ */}
            {/* ICON CLOUD                                                        */}
            {/* ================================================================ */}

            <div
              className="
                relative
                mt-[-4px]
                flex
                h-[150px]
                w-full
                items-center
                justify-center
                overflow-visible

                min-[380px]:h-[165px]

                sm:h-[185px]

                md:h-[210px]

                lg:mt-[-10px]
                lg:h-[235px]

                xl:h-[250px]
                xl:translate-x-10
              "
            >
              <IconCloud3D
                images={images}
                radius={100}
                rotationSpeed={0.006}
                maxIcons={32}
                iconSize={20}
                radiusJitter={7}
                className="h-full w-full"
              />
            </div>
          </div>
        </div>

        {/* ==================================================================== */}
        {/* RESPONSIVE ANIMATIONS                                                 */}
        {/* ==================================================================== */}

        <style>{`
          /* ================================================================== */
          /* DESKTOP VERTICAL MARQUEE                                            */
          /* ================================================================== */

          @keyframes marquee-v {
            0% {
              transform: translateY(0);
            }

            100% {
              transform: translateY(-33.333333%);
            }
          }

          .animate-marquee-v {
            animation: marquee-v 22s linear infinite;
            will-change: transform;
          }

          .animate-marquee-v-reverse {
            animation: marquee-v 26s linear infinite reverse;
            will-change: transform;
          }

          /* ================================================================== */
          /* MOBILE ROW 1                                                       */
          /* ================================================================== */

          @keyframes marquee-horizontal-left {
            0% {
              transform: translateX(0);
            }

            100% {
              transform: translateX(-33.333333%);
            }
          }

          /* ================================================================== */
          /* MOBILE ROW 2                                                       */
          /* ================================================================== */

          @keyframes marquee-horizontal-right {
            0% {
              transform: translateX(-33.333333%);
            }

            100% {
              transform: translateX(0);
            }
          }

          .animate-marquee-horizontal-left {
            animation: marquee-horizontal-left 28s linear infinite;
            will-change: transform;
          }

          .animate-marquee-horizontal-right {
            animation: marquee-horizontal-right 32s linear infinite;
            will-change: transform;
          }

          /* ================================================================== */
          /* DESKTOP VERTICAL EDGE FADE                                          */
          /* ================================================================== */

          .mask-fade-vertical {
            mask-image: linear-gradient(
              to bottom,
              transparent,
              black 10%,
              black 90%,
              transparent
            );

            -webkit-mask-image: linear-gradient(
              to bottom,
              transparent,
              black 10%,
              black 90%,
              transparent
            );
          }

          /* ================================================================== */
          /* HOVER PAUSE                                                         */
          /* ================================================================== */

          .animate-marquee-v:hover,
          .animate-marquee-v-reverse:hover,
          .animate-marquee-horizontal-left:hover,
          .animate-marquee-horizontal-right:hover {
            animation-play-state: paused;
          }

          /* ================================================================== */
          /* TABLET                                                              */
          /* ================================================================== */

          @media (max-width: 1023px) {
            .animate-marquee-v {
              animation-duration: 24s;
            }

            .animate-marquee-v-reverse {
              animation-duration: 28s;
            }
          }

          /* ================================================================== */
          /* MOBILE                                                              */
          /* ================================================================== */

          @media (max-width: 767px) {
            .animate-marquee-horizontal-left {
              animation-duration: 28s;
            }

            .animate-marquee-horizontal-right {
              animation-duration: 32s;
            }
          }

          /* ================================================================== */
          /* SMALL MOBILE                                                        */
          /* ================================================================== */

          @media (max-width: 480px) {
            .animate-marquee-horizontal-left {
              animation-duration: 30s;
            }

            .animate-marquee-horizontal-right {
              animation-duration: 34s;
            }
          }

          /* ================================================================== */
          /* VERY SMALL MOBILE                                                   */
          /* ================================================================== */

          @media (max-width: 380px) {
            .animate-marquee-horizontal-left {
              animation-duration: 34s;
            }

            .animate-marquee-horizontal-right {
              animation-duration: 38s;
            }
          }

          /* ================================================================== */
          /* REDUCED MOTION                                                      */
          /* ================================================================== */

          @media (prefers-reduced-motion: reduce) {
            .animate-marquee-v,
            .animate-marquee-v-reverse,
            .animate-marquee-horizontal-left,
            .animate-marquee-horizontal-right {
              animation-play-state: paused;
            }
          }
        `}</style>
      </section>

      {/* ====================================================================== */}
      {/* CONTACT MODAL                                                          */}
      {/* ====================================================================== */}

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
};

export default ClientSection;